import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../Modal/Modal';
import { DictItem } from '../../services/dicts.service';
import ticketService from '../../services/ticket.service';
import entityDocumentsService from '../../services/entity-documents.service';
import { TicketDetails } from '../../types/ticket.types';
import { showToast } from '../../utils/toast';
import customerService from '../../services/customer.service';
import { CustomerListItemV2 } from '../../types/customer.types';
import usersService from '../../services/users.service';
import dictsService, { DictItem as TaskDictItem } from '../../services/dicts.service';
import type { GroupUser } from '../../types/group.types';
import emailService from '../../services/email.service';
import type { EmailAccount } from '../../types/email.types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dicts: {
    statuses: DictItem[];
    types: DictItem[];
    priorities: DictItem[];
    slas: DictItem[];
    labels: DictItem[];
  };
  onCreated?: (ticket: TicketDetails) => void;
}

const DROPDOWN_Z_INDEX = 2147483605; // чуть ниже глобального модального окна

const TicketCreateModal: React.FC<Props> = ({ isOpen, onClose, dicts, onCreated }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority_id: null as number | null,
    type_id: null as number | null,
    sla_id: null as number | null,
    customer_id: null as number | null,
    reporter_email: '',
    from_address: '',
    to_address: '',
  });

  const typesById = useMemo(() => Object.fromEntries(dicts.types.map(d => [d.id, d])), [dicts.types]);
  const selectedTypeCode = useMemo(() => (form.type_id ? typesById[form.type_id]?.code : undefined), [form.type_id, typesById]);
  const prioritiesById = useMemo(() => Object.fromEntries(dicts.priorities.map(p => [p.id, p])), [dicts.priorities]);

  // Attachments state (добавляем до создания тикета, загружаем после)
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Optional task creation state
  const [addTask, setAddTask] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskPriorityId, setTaskPriorityId] = useState<number | null>(null);
  const [taskTypeId, setTaskTypeId] = useState<number | null>(null);
  const [taskDueAt, setTaskDueAt] = useState('');
  const [, setTaskAssigneeId] = useState<number | null>(null);
  const [taskAssigneeError, setTaskAssigneeError] = useState(false);
  const [taskTypes, setTaskTypes] = useState<TaskDictItem[]>([]);
  const taskTypesById = useMemo(() => Object.fromEntries(taskTypes.map(tt => [tt.id, tt])), [taskTypes]);
  const [taskTypesLoading, setTaskTypesLoading] = useState(false);
  const [users, setUsers] = useState<GroupUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersQuery, setUsersQuery] = useState('');
  const assigneeInputRef = useRef<HTMLInputElement | null>(null);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [assigneePos, setAssigneePos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [assigneeHighlighted, setAssigneeHighlighted] = useState(0);
  // Дополнительные задачи (простой ввод ID исполнителя)
  const [tasksExtra, setTasksExtra] = useState<Array<{
    title: string;
    description: string;
    assignee_id: number | null;
    assignee_error?: boolean;
    assignee_display: string;
    due_at: string;
    priority_id: number | null;
    task_type_id: number | null;
    expanded?: boolean;
  }>>([]);
  const [activeExtraIdx, setActiveExtraIdx] = useState<number | null>(null);

  // Customer search state
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState<CustomerListItemV2[]>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerListItemV2 | null>(null);
  const customerBoxRef = useRef<HTMLDivElement | null>(null);

  // Email accounts for to_address combobox (only for email type)
  const [toOpen, setToOpen] = useState(false);
  const [toLoading, setToLoading] = useState(false);
  const [toAccounts, setToAccounts] = useState<EmailAccount[]>([]);
  const toBoxRef = useRef<HTMLDivElement | null>(null);

  const onFilesSelected = (list: FileList | null) => {
    if (!list) return;
    const next: File[] = [...files];
    for (let i = 0; i < list.length; i++) {
      const f = list.item(i);
      if (f) next.push(f);
    }
    setFiles(next);
  };

  const removeFileAt = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    if (!isOpen) return;
    // Устанавливаем дефолты при открытии
    setForm((prev) => ({
      ...prev,
      priority_id: prev.priority_id ?? (dicts.priorities[0]?.id ?? null),
      type_id: prev.type_id ?? (dicts.types[0]?.id ?? null),
      sla_id: prev.sla_id ?? (dicts.slas[0]?.id ?? null),
    }));
    setError(null);
    // Сброс выбора клиента при новом открытии
    setSelectedCustomer(null);
    setCustomerQuery('');
    setCustomerResults([]);
    setCustomerDropdownOpen(false);
    // reset task block
    setAddTask(false);
    setTaskTitle('');
    setTaskDescription('');
    setTaskPriorityId(dicts.priorities[0]?.id ?? null);
    setTaskTypeId(null);
    setTaskDueAt('');
    setTaskAssigneeId(null);
    setTaskAssigneeError(false);
  }, [isOpen, dicts.priorities, dicts.types, dicts.slas]);

  // Debounced search for customers
  useEffect(() => {
    let cancelled = false;
    const q = customerQuery.trim();
    if (!customerDropdownOpen) return;
    if (!q) {
      setCustomerResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setCustomerLoading(true);
        const page = await customerService.getCustomers({ skip: 0, limit: 10, search: q });
        if (!cancelled) setCustomerResults(page.items || []);
      } catch (e) {
        if (!cancelled) setCustomerResults([]);
      } finally {
        if (!cancelled) setCustomerLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [customerQuery, customerDropdownOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (customerBoxRef.current && !customerBoxRef.current.contains(target)) {
        setCustomerDropdownOpen(false);
      }
    };
    if (customerDropdownOpen) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [customerDropdownOpen]);

  // Close to_address dropdown on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (toBoxRef.current && !toBoxRef.current.contains(target)) {
        setToOpen(false);
      }
    };
    if (toOpen) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [toOpen]);

  // Lazy load email accounts when to dropdown opens (only once)
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      if (!toOpen) return;
      if (toAccounts.length > 0) return;
      try {
        setToLoading(true);
        const list = await emailService.listAccounts();
        const enabled = (list || []).filter(a => a.enabled);
        if (!aborted) setToAccounts(enabled);
      } catch {
        if (!aborted) setToAccounts([]);
      } finally {
        if (!aborted) setToLoading(false);
      }
    };
    void load();
    return () => { aborted = true; };
  }, [toOpen, toAccounts.length]);

  // Load task types when there are tasks
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      if (tasksExtra.length === 0) return;
      try {
        setTaskTypesLoading(true);
        const items = await dictsService.listDictItems('ticket_task_types');
        if (!aborted) setTaskTypes(items || []);
      } catch {
        if (!aborted) setTaskTypes([]);
      } finally {
        if (!aborted) setTaskTypesLoading(false);
      }
    };
    void load();
    return () => { aborted = true; };
  }, [tasksExtra.length]);

  // Users search for assignee (debounced)
  useEffect(() => {
    if (!isOpen || activeExtraIdx === null) return;
    let aborted = false;
    const t = setTimeout(async () => {
      try {
        setUsersLoading(true);
        const list = await usersService.searchUsers(usersQuery || undefined, 0, 50);
        if (!aborted) setUsers((list || []).filter(u => u.is_active));
      } catch {
        if (!aborted) setUsers([]);
      } finally {
        if (!aborted) setUsersLoading(false);
      }
    }, 300);
    return () => { aborted = true; clearTimeout(t); };
  }, [isOpen, usersQuery, activeExtraIdx]);

  const updateAssigneePos = () => {
    let el: HTMLElement | null = null;
    if (activeExtraIdx != null) {
      el = document.getElementById(`extra-assignee-input-${activeExtraIdx}`);
    } else {
      el = assigneeInputRef.current;
    }
    if (!el) { setAssigneePos(null); return; }
    const r = el.getBoundingClientRect();
    setAssigneePos({ top: r.bottom + 4, left: r.left, width: r.width });
  };
  useEffect(() => {
    if (!assigneeOpen) return;
    updateAssigneePos();
    const onScroll = () => updateAssigneePos();
    const onResize = () => updateAssigneePos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onResize); };
  }, [assigneeOpen]);
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const within = assigneeInputRef.current && assigneeInputRef.current.contains(target as Node);
      const dropdown = document.getElementById('create-task-assignee-list');
      const withinDropdown = dropdown && dropdown.contains(target);
      if (!within && !withinDropdown) setAssigneeOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={() => { if (!submitting) onClose(); }} title="Новый тикет" size="lg">
      {error && (
        <div className="alert alert-error mb-4">
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-5h2v2h-2v-2zm0-8h2v6h-2V5z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          if (!form.title.trim()) { setError('Укажите заголовок'); return; }
          try {
            setSubmitting(true);
            // Попробуем определить account_id по to_address (email аккаунт получателя)
            let accountId: number | undefined;
            if (selectedTypeCode === 'email') {
              const to = (form.to_address || '').trim().toLowerCase();
              if (to) {
                try {
                  // Переиспользуем загруженные toAccounts, если есть; иначе загрузим
                  const list = toAccounts.length ? toAccounts : (await emailService.listAccounts());
                  const acc = (list || []).find(a => a.enabled && (a.email_address || '').toLowerCase() === to);
                  if (acc?.id != null) accountId = Number(acc.id);
                } catch {
                  // тихо игнорируем, не блокируем создание тикета
                }
              }
            }
            // Блокируем создание, если не нашли аккаунт для получателя
            if (selectedTypeCode === 'email' && !accountId) {
              setError('Не найден email-аккаунт для адреса получателя (to_address). Выберите адрес из списка аккаунтов.');
              setSubmitting(false);
              return;
            }

            const payload = {
              title: form.title.trim(),
              description: form.description.trim() || null,
              status_id: 1,
              priority_id: form.priority_id ?? undefined,
              type_id: form.type_id ?? undefined,
              sla_id: form.sla_id ?? undefined,
              customer_id: form.customer_id ?? undefined,
              reporter_email: selectedTypeCode === 'email' ? (form.from_address.trim() || form.reporter_email.trim() || undefined) : undefined,
              from_address: selectedTypeCode === 'email' ? (form.from_address.trim() || undefined) : undefined,
              to_address: selectedTypeCode === 'email' ? (form.to_address.trim() || undefined) : undefined,
              account_id: selectedTypeCode === 'email' ? (accountId ?? null) : undefined,
            };
            if (selectedTypeCode === 'email') {
              if (!payload.from_address) { setError('Укажите поле "От (from_address)"'); return; }
              if (!payload.to_address) { setError('Укажите поле "Кому (to_address)"'); return; }
            }
            // Add tasks into payload if any exist
            if (tasksExtra.length > 0) {
              const prepared = tasksExtra.map((t, i) => {
                if (t.assignee_id == null) {
                  setTasksExtra((list) => list.map((it, idx) => idx === i ? { ...it, assignee_error: true } : it));
                }
                return {
                  title: t.title?.trim() || `Задача ${i + 1}`,
                  description: t.description?.trim() || null,
                  assignee_id: t.assignee_id as number,
                  due_at: t.due_at ? new Date(t.due_at).toISOString() : null,
                  priority_id: t.priority_id ?? null,
                  task_type_id: t.task_type_id ?? null,
                };
              });
              if (tasksExtra.some(t => t.assignee_id == null)) { setSubmitting(false); return; }
              if (prepared.length === 1) (payload as any).task = prepared[0];
              else (payload as any).tasks = prepared;
            }
            const created = await ticketService.createTicket(payload);
            // После успешного создания — если есть файлы, загружаем вложения с прогрессом
            if (files.length > 0) {
              try {
                setUploadProgress(0);
                await entityDocumentsService.uploadDocuments({
                  entityType: 'tickets',
                  entityId: created.id,
                  files,
                });
                setUploadProgress(100);
                showToast('Вложения загружены', 'success', 1200);
              } catch (e: any) {
                showToast(e?.message || 'Не удалось загрузить вложения', 'error', 1500);
              }
            }
            showToast('Тикет создан', 'success', 1500);
            try {
              window.dispatchEvent(new CustomEvent('tickets:changed', { detail: { action: 'created', id: created.id } }));
            } catch (e) { void e; }
            onClose();
            onCreated?.(created);
          } catch (err: any) {
            setError(err?.message || 'Ошибка создания тикета');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <div className="grid-2col">
          <div>
            <div className="form-group">
              <label className="form-label">Заголовок</label>
              <input
                className="input"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Введите заголовок"
                disabled={submitting}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Описание</label>
              <textarea
                className="input"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Опишите задачу"
                rows={6}
                disabled={submitting}
              />
            </div>
            {selectedTypeCode === 'email' && (
              <>
                <div className="form-group">
                  <label className="form-label">От (from_address)</label>
                  <input
                    className="input"
                    type="email"
                    value={form.from_address}
                    onChange={(e) => setForm((p) => ({ ...p, from_address: e.target.value }))}
                    placeholder="sender@example.com"
                    disabled={submitting}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Кому (to_address)</label>
                  <div ref={toBoxRef} className="relative">
                    <input
                      className="input"
                      type="email"
                      value={form.to_address}
                      onChange={(e) => setForm((p) => ({ ...p, to_address: e.target.value }))}
                      onFocus={() => setToOpen(true)}
                      placeholder="recipient@example.com"
                      disabled={submitting}
                      required
                    />
                    {toOpen && (
                      <div
                        className="absolute top-full left-0 right-0 bg-white border-subtle-1 rounded-lg overflow-y-auto" style={{ boxShadow: 'var(--shadow-lg)', maxHeight: 240, zIndex: DROPDOWN_Z_INDEX }}
                      >
                        {toLoading ? (
                          <div className="text-muted p-8px text-xs">Загрузка…</div>
                        ) : (
                          (() => {
                            const q = (form.to_address || '').trim().toLowerCase();
                            const list = q ? toAccounts.filter(a => a.email_address.toLowerCase().includes(q)) : toAccounts;
                            if (list.length === 0) {
                              return <div className="text-muted p-8px text-xs">Нет подходящих аккаунтов</div>;
                            }
                            return (
                              <div>
                                {list.map(acc => (
                                  <button
                                    key={acc.id}
                                    type="button"
                                    className="btn-ghost w-full justify-start"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => { setForm((p) => ({ ...p, to_address: acc.email_address })); setToOpen(false); }}
                                  >
                                    {acc.email_address}
                                  </button>
                                ))}
                              </div>
                            );
                          })()
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-muted text-xs mt-6px">
                    Можно ввести email вручную или выбрать из списка
                  </div>
                </div>
              </>
            )}

            {/* Attachments uploader */}
            <div className="form-group">
              <label className="form-label">Вложения</label>
              <div
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation(); setDragActive(false);
                  onFilesSelected(e.dataTransfer.files);
                }}
                style={{
                  border: '1.5px dashed var(--border-subtle)',
                  background: dragActive ? 'var(--gray-50)' : 'transparent',
                  borderRadius: 'var(--radius-lg)',
                  padding: '16px',
                  textAlign: 'center',
                }}
              >
                <div className="text-gray-600 text-sm mb-8px">
                  Перетащите файлы сюда или выберите вручную
                </div>
                <label className="btn-secondary cursor-pointer">
                  Выбрать файлы
                  <input type="file" multiple onChange={(e) => onFilesSelected(e.target.files)} disabled={submitting} className="d-none" />
                </label>
              </div>
              {files.length > 0 && (
                <ul className="p-0 flex flex-col-dir gap-6px" style={{ listStyle: 'none', marginTop: 10 }}>
                  {files.map((f, idx) => (
                    <li key={`${f.name}-${idx}`} className="flex items-center justify-between border-subtle-1 rounded-md px-10-py-8">
                      <div className="flex-center-g8">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <path d="M14 2v6h6" />
                        </svg>
                        <span className="text-sm">{f.name}</span>
                        <span className="text-xs-gray-500">{(f.size / 1024).toFixed(1)} KB</span>
                      </div>
                      <button type="button" className="btn-icon btn-icon-outline" onClick={() => removeFileAt(idx)} aria-label="Удалить файл">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--error-500)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M4 7h16" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12" />
                          <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {submitting && files.length > 0 && (
              <div className="mt-8px">
                <div className="flex items-center justify-between mb-6px">
                  <span className="muted text-xs">Загрузка вложений...</span>
                  <span className="muted text-xs">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200" style={{ height: 6, borderRadius: 999 }}>
                  <div style={{ width: `${uploadProgress}%`, height: '100%', background: 'var(--primary-500)', borderRadius: 999, transition: 'width 0.2s ease' }} />
                </div>
              </div>
            )}
          </div>
          <div>
            {/* Customer selector */}
            <div className="form-group" ref={customerBoxRef}>
              <label className="form-label">Клиент</label>
              {selectedCustomer ? (
                <div className="flex-center-g8">
                  <div className="status-badge px-8-py-4">
                    {selectedCustomer.full_name || `${selectedCustomer.private_first_name ?? ''} ${selectedCustomer.private_last_name ?? ''}`.trim() || `ID ${selectedCustomer.id}`}
                    {selectedCustomer.identification_no ? ` · ${selectedCustomer.identification_no}` : ''}
                  </div>
                  <button
                    type="button"
                    className="btn-icon btn-icon-outline"
                    onClick={() => {
                      setSelectedCustomer(null);
                      setForm((p) => ({ ...p, customer_id: null }));
                    }}
                    aria-label="Очистить выбор клиента"
                    title="Очистить выбор"
                    disabled={submitting}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--error-500)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    className="input"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    onFocus={() => setCustomerDropdownOpen(true)}
                    placeholder="Начните ввод: ФИО, ИИН/ИНН, email, телефон"
                    disabled={submitting}
                  />
                  {customerDropdownOpen && (
                    <div
                      className="absolute top-full left-0 right-0 bg-surface-white border-subtle-1 rounded-md mt-4px overflow-y-auto" style={{ zIndex: 20, maxHeight: 240, boxShadow: 'var(--shadow-sm)' }}
                    >
                      {customerLoading ? (
                        <div className="text-gray-600 text-sm" style={{ padding: 10 }}>
                          Поиск...
                        </div>
                      ) : (customerResults.length > 0 ? (
                        customerResults.map((c) => {
                          const name = c.full_name || `${c.private_first_name ?? ''} ${c.private_last_name ?? ''}`.trim() || `ID ${c.id}`;
                          const sub = [c.identification_no, c.region_declared, c.address_declared_city]
                            .filter(Boolean)
                            .join(' · ');
                          const email = (c.emails && c.emails[0]?.email) ? c.emails[0].email : undefined;
                          const phone = (c.phones && c.phones[0]?.number) ? c.phones[0].number : undefined;
                          const meta = [sub, email, phone].filter(Boolean).join(' · ');
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setSelectedCustomer(c);
                                setForm((p) => ({ ...p, customer_id: c.id }));
                                setCustomerDropdownOpen(false);
                              }}
                              className="w-full text-left bg-transparent cursor-pointer focus-ring" style={{ padding: '8px 10px', border: 0 }}
                            >
                              <div className="text-sm font-medium">{name}</div>
                              {meta && <div className="muted text-xs">{meta}</div>}
                            </button>
                          );
                        })
                      ) : (
                        <div className="text-gray-600 text-sm" style={{ padding: 10 }}>
                          {customerQuery.trim() ? 'Ничего не найдено' : 'Введите минимум 1 символ для поиска'}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Приоритет</label>
              <select
                className="input"
                value={form.priority_id ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, priority_id: e.target.value ? Number(e.target.value) : null }))}
                disabled={submitting}
              >
                <option value="">—</option>
                {dicts.priorities.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Тип</label>
              <select
                className="input"
                value={form.type_id ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, type_id: e.target.value ? Number(e.target.value) : null }))}
                disabled={submitting}
              >
                <option value="">—</option>
                {dicts.types.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">SLA</label>
              <select
                className="input"
                value={form.sla_id ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, sla_id: e.target.value ? Number(e.target.value) : null }))}
                disabled={submitting}
              >
                <option value="">—</option>
                {dicts.slas.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {/* Optional task block */}
        <div className="content-card mt-16px">
          <div className="card-body p-4 flex flex-col-dir gap-12px">
            <button
              type="button"
              className="btn-secondary self-start d-inline-flex items-center gap-10px"
              onClick={() => setTasksExtra((list) => [
                ...list,
                {
                  title: '',
                  description: '',
                  assignee_id: null,
                  assignee_display: '',
                  due_at: '',
                  priority_id: dicts.priorities[0]?.id ?? null,
                  task_type_id: null,
                  expanded: true,
                },
              ])}
              disabled={submitting}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 5v14"/>
                <path d="M5 12h14"/>
              </svg>
              Добавить задачу
            </button>
            {false && (
              <div className="grid-2col">
                <div>
                  <div className="form-group flex items-center justify-between gap-8px">
                    <label className="form-label">Заголовок задачи</label>
                    <button type="button" className="btn-icon btn-icon-outline" aria-label="Удалить задачу" title="Удалить задачу" onClick={() => { setTaskTitle(''); setTaskDescription(''); setTaskAssigneeId(null); setUsersQuery(''); setTaskAssigneeError(false); setAddTask(false); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                  <input className="input" value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Например: Связаться с клиентом" disabled={submitting} />
                  <div className="form-group">
                    <label className="form-label">Описание задачи</label>
                    <textarea className="input" rows={4} value={taskDescription} onChange={(e) => setTaskDescription(e.target.value)} placeholder="Кратко опишите суть" disabled={submitting} />
                  </div>
                </div>
                <div>
                  <div className="form-group">
                    <label className="form-label">Исполнитель <span className="text-muted">(обязательно)</span></label>
                    <input
                      ref={assigneeInputRef}
                      className={`input ${taskAssigneeError ? 'input-error' : ''}`}
                      placeholder="Начните вводить имя или email"
                      value={usersQuery}
                      onChange={(e) => { setUsersQuery(e.target.value); setAssigneeOpen(true); setAssigneeHighlighted(0); if (taskAssigneeError) setTaskAssigneeError(false); }}
                      onFocus={() => { setAssigneeOpen(true); updateAssigneePos(); }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') { e.preventDefault(); setAssigneeHighlighted((i) => Math.min(i + 1, users.length - 1)); }
                        if (e.key === 'ArrowUp') { e.preventDefault(); setAssigneeHighlighted((i) => Math.max(i - 1, 0)); }
                        if (e.key === 'Enter' && assigneeOpen && users.length > 0) {
                          e.preventDefault();
                          const u = users[assigneeHighlighted] || users[0];
                          setTaskAssigneeId(u.id);
                          setUsersQuery(`${u.full_name || u.username} (${u.email})`);
                          setAssigneeOpen(false);
                          if (taskAssigneeError) setTaskAssigneeError(false);
                        }
                      }}
                      disabled={submitting || !addTask}
                      aria-expanded={assigneeOpen}
                      aria-controls="create-task-assignee-list"
                    />
                    {assigneeOpen && assigneePos && (
                      <div
                        id="create-task-assignee-list"
                        role="listbox"
                        className="fixed overflow-y-auto bg-white border-subtle-1 rounded-lg" style={{ top: assigneePos!.top, left: assigneePos!.left, width: assigneePos!.width, maxHeight: 240, boxShadow: 'var(--shadow-lg)', zIndex: 2000 }}
                      >
                        {usersLoading ? (
                          <div className="text-muted p-8px text-xs">Загрузка…</div>
                        ) : users.length === 0 ? (
                          <div className="text-muted p-8px text-xs">Ничего не найдено</div>
                        ) : (
                          users.map((u, idx) => (
                            <button
                              key={u.id}
                              type="button"
                              className="btn-ghost"
                              style={{ width: '100%', justifyContent: 'flex-start', background: idx === assigneeHighlighted ? 'var(--gray-100)' : undefined }}
                              onMouseEnter={() => setAssigneeHighlighted(idx)}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { setTaskAssigneeId(u.id); setUsersQuery(`${u.full_name || u.username} (${u.email})`); setAssigneeOpen(false); if (taskAssigneeError) setTaskAssigneeError(false); }}
                            >
                              {u.full_name || u.username} ({u.email})
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Срок (дата и время)</label>
                    <input type="datetime-local" className="input" value={taskDueAt} onChange={(e) => setTaskDueAt(e.target.value)} disabled={submitting} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Приоритет задачи</label>
                    <select className="input" value={taskPriorityId ?? ''} onChange={(e) => setTaskPriorityId(e.target.value ? Number(e.target.value) : null)} disabled={submitting}>
                      <option value="">—</option>
                      {dicts.priorities.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Тип задачи</label>
                    <select className="input" value={taskTypeId ?? ''} onChange={(e) => setTaskTypeId(e.target.value ? Number(e.target.value) : null)} disabled={submitting || taskTypesLoading}>
                      <option value="">—</option>
                      {taskTypes.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                    </select>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-12px">
                {tasksExtra.length === 0 ? (
                  <div className="muted text-sm">Задач пока нет</div>
                ) : (
                  <div className="flex flex-col-dir gap-12px">
                    {tasksExtra.map((t, idx) => (
                      <div
                        key={idx}
                        className="content-card content-card--minimal p-3 border-subtle-1 rounded-md"
                      >
                        <div className="flex justify-between items-center gap-12px">
                          <button
                            type="button"
                            className="btn-ghost inline-flex-center-g8"
                            onClick={() => setTasksExtra((list) => list.map((it, i) => i === idx ? { ...it, expanded: !it.expanded } : it))}
                            aria-expanded={t.expanded ?? true}
                            title={(t.expanded ?? true) ? 'Свернуть' : 'Развернуть'}
                          >
                            <svg
                              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
                              style={{ transform: (t.expanded ?? true) ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .15s ease' }}
                            >
                              <polyline points="6 9 12 15 18 9"/>
                            </svg>
                            <span className="font-semibold">Добавить задачу к тикету</span>
                            {!(t.expanded ?? true) && (
                              <span style={{ color: 'var(--gray-700)', display: 'inline-flex', gap: 6, alignItems: 'center', fontWeight: 400, fontSize: 'var(--text-sm)', marginLeft: 6 }}>
                                {t.priority_id != null && (
                                  <span className="status-badge px-6-py-2">
                                    {prioritiesById[t.priority_id]?.name ?? 'Приоритет —'}
                                  </span>
                                )}
                                {t.task_type_id != null && (
                                  <span className="status-badge px-6-py-2">
                                    {taskTypesById[t.task_type_id]?.name ?? 'Тип —'}
                                  </span>
                                )}
                                <span className="status-badge px-6-py-2">
                                  {t.assignee_display?.trim() ? t.assignee_display : 'Нет исполнителя'}
                                </span>
                                <span className="status-badge px-6-py-2">
                                  {(() => {
                                    const s = (t.due_at || '').trim();
                                    if (!s) return 'Без срока';
                                    // s expected YYYY-MM-DDTHH:mm
                                    const [date, time] = s.split('T');
                                    if (!date) return s;
                                    const [y, m, d] = date.split('-');
                                    const hhmm = (time || '').slice(0,5);
                                    return `${d}.${m}.${y} ${hhmm}`;
                                  })()}
                                </span>
                              </span>
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setTasksExtra((list) => list.filter((_, i) => i !== idx))}
                          >
                            Удалить
                          </button>
                        </div>
                        {(t.expanded ?? true) && (
                        <div className="grid-2col mt-8px">
                          <div>
                            <div className="form-group">
                              <label className="form-label">Заголовок</label>
                              <input className="input" value={t.title} onChange={(e) => setTasksExtra((list) => list.map((it, i) => i === idx ? { ...it, title: e.target.value } : it))} placeholder="Например: Проверить шлюз" disabled={submitting} />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Описание</label>
                              <textarea className="input" rows={3} value={t.description} onChange={(e) => setTasksExtra((list) => list.map((it, i) => i === idx ? { ...it, description: e.target.value } : it))} placeholder="Дополнительно" disabled={submitting} />
                            </div>
                          </div>
                          <div>
                  <div className="form-group">
                    <label className="form-label">Исполнитель <span className="text-muted">(обязательно)</span></label>
                    <input
                      id={`extra-assignee-input-${idx}`}
                      className={`input ${t.assignee_error ? 'input-error' : ''}`}
                      placeholder="Начните вводить имя или email"
                      value={activeExtraIdx === idx ? usersQuery : (t.assignee_display || '')}
                      onFocus={() => { setActiveExtraIdx(idx); setUsersQuery(t.assignee_display || ''); setAssigneeOpen(true); setAssigneeHighlighted(0); updateAssigneePos(); }}
                      onChange={(e) => { setActiveExtraIdx(idx); setUsersQuery(e.target.value); setAssigneeOpen(true); setAssigneeHighlighted(0); if (t.assignee_error) setTasksExtra((list)=>list.map((it,i)=>i===idx?{...it,assignee_error:false}:it)); }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') { e.preventDefault(); setAssigneeHighlighted((i) => Math.min(i + 1, users.length - 1)); }
                        if (e.key === 'ArrowUp') { e.preventDefault(); setAssigneeHighlighted((i) => Math.max(i - 1, 0)); }
                        if (e.key === 'Enter' && assigneeOpen && users.length > 0) {
                          e.preventDefault();
                          const u = users[assigneeHighlighted] || users[0];
                          const display = `${u.full_name || u.username} (${u.email})`;
                          setTasksExtra((list) => list.map((it, i) => i === idx ? { ...it, assignee_id: u.id, assignee_display: display, assignee_error: false } : it));
                          setUsersQuery(display);
                          setAssigneeOpen(false);
                          setActiveExtraIdx(null);
                        }
                      }}
                      disabled={submitting}
                      aria-expanded={assigneeOpen}
                      aria-controls="create-task-assignee-list"
                    />
                    {assigneeOpen && assigneePos && activeExtraIdx === idx && (
                      <div
                        id="create-task-assignee-list"
                        role="listbox"
                        className="fixed overflow-y-auto bg-white border-subtle-1 rounded-lg" style={{ top: assigneePos!.top, left: assigneePos!.left, width: assigneePos!.width, maxHeight: 240, boxShadow: 'var(--shadow-lg)', zIndex: 2000 }}
                      >
                        {usersLoading ? (
                          <div className="text-muted p-8px text-xs">Загрузка…</div>
                        ) : users.length === 0 ? (
                          <div className="text-muted p-8px text-xs">Ничего не найдено</div>
                        ) : (
                          users.map((u, uidx) => (
                            <button
                              key={u.id}
                              type="button"
                              className="btn-ghost"
                              style={{ width: '100%', justifyContent: 'flex-start', background: uidx === assigneeHighlighted ? 'var(--gray-100)' : undefined }}
                              onMouseEnter={() => setAssigneeHighlighted(uidx)}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                const display = `${u.full_name || u.username} (${u.email})`;
                                setTasksExtra((list) => list.map((it, i) => i === idx ? { ...it, assignee_id: u.id, assignee_display: display, assignee_error: false } : it));
                                setUsersQuery(display);
                                setAssigneeOpen(false);
                                setActiveExtraIdx(null);
                              }}
                            >
                              {u.full_name || u.username} ({u.email})
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                            <div className="form-group">
                              <label className="form-label">Срок</label>
                              <input type="datetime-local" className="input" value={t.due_at} onChange={(e) => setTasksExtra((list) => list.map((it, i) => i === idx ? { ...it, due_at: e.target.value } : it))} disabled={submitting} />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Приоритет</label>
                              <select className="input" value={t.priority_id ?? ''} onChange={(e) => setTasksExtra((list) => list.map((it, i) => i === idx ? { ...it, priority_id: e.target.value ? Number(e.target.value) : null } : it))} disabled={submitting}>
                                <option value="">—</option>
                                {dicts.priorities.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="form-label">Тип задачи</label>
                              <select className="input" value={t.task_type_id ?? ''} onChange={(e) => setTasksExtra((list) => list.map((it, i) => i === idx ? { ...it, task_type_id: e.target.value ? Number(e.target.value) : null } : it))} disabled={submitting || taskTypesLoading}>
                                <option value="">—</option>
                                {taskTypes.map((tt) => (<option key={tt.id} value={tt.id}>{tt.name}</option>))}
                              </select>
                            </div>
                          </div>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        <div className="form-actions mt-12px flex justify-end gap-8px">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Отмена</button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? (<><span className="spinner" /> Создание...</>) : 'Создать'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TicketCreateModal;
